/**
 * UpsellProduct — HUB 2 Hose Extension Auto-Match
 *
 * Custom element that manages the upsell product block on the HUB 2 product page.
 *
 * AUTO-MATCH MODE (data-hub-auto-match="true"):
 *   Automatically resolves the correct Hose Extension variant and quantity
 *   based on the selected HUB mattress size. Hooks into the theme's native
 *   VariantUpdateEvent so it shares the same lifecycle as the rest of the PDP.
 *
 * MANUAL MODE (data-hub-auto-match="false"):
 *   Original behaviour — user selects zone via the dropdown selector.
 *
 * HUB SIZE → EXTENSION MAPPING:
 *   Single / Long Single / King Single  → Single-Zone × 1
 *   Double / Queen / King               → Dual-Zone × 1
 *   Super King / Split King             → Single-Zone × 2
 *
 * ZONE RESOLUTION (no hardcoded variant IDs):
 *   Reads the embedded variants JSON and finds the first variant whose option
 *   values contain "single" (for Single-Zone) or "dual" (for Dual-Zone).
 *   This survives handle changes or variant reordering.
 *
 * INVENTORY:
 *   qty 1 requires available: true and inventory_quantity >= 1
 *   qty 2 requires available: true and inventory_quantity >= 2
 *
 * DUPLICATE PREVENTION:
 *   Checks cart before adding. If exact variant+quantity already present, shows
 *   "Added" state without adding again.
 *
 * LINE ITEM PROPERTY:
 *   Adds "_Matched to" property (prefixed with _ to hide in storefront) so the
 *   extension item is identifiable in orders. Customers never see the raw key.
 */

class UpsellProduct extends HTMLElement {
  constructor() {
    super();

    // DOM refs
    this.button = this.querySelector('[data-upsell-add-btn]');
    this.errorEl = this.querySelector('[data-upsell-error]');
    this.btnPriceEl = this.querySelector('[data-upsell-btn-price]');
    this.matchLabelEl = this.querySelector('[data-upsell-match-label]');
    this.unavailableEl = this.querySelector('[data-upsell-unavailable]');

    // State
    this.upsellVariantId = this.dataset.upsellVariantId;
    this.upsellQuantity = parseInt(this.dataset.upsellQuantity || '1', 10);
    this.isAutoMatch = this.dataset.hubAutoMatch === 'true';

    // Variants JSON (embedded by Liquid)
    const variantsScript = this.querySelector('[data-upsell-variants-json]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

    // Manual mode: option selects
    this.variantSelects = this.querySelectorAll('[data-upsell-option-select]');

    // Abort controller for event cleanup
    this._controller = new AbortController();
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this._handleClick.bind(this), {
        signal: this._controller.signal
      });
    }

    if (this.isAutoMatch) {
      this._initAutoMatch();
    } else {
      this._initManual();
    }
  }

  disconnectedCallback() {
    this._controller.abort();
  }

  // ─────────────────────────────────────────────────────────
  // MANUAL MODE
  // ─────────────────────────────────────────────────────────

  _initManual() {
    this.variantSelects.forEach(select => {
      select.addEventListener('change', () => this._updateSelectedVariant(), {
        signal: this._controller.signal
      });
    });
    if (this.variantSelects.length && this.variants.length) {
      this._updateSelectedVariant();
    }
  }

  _updateSelectedVariant() {
    const selectedOptions = Array.from(this.variantSelects).map(s => s.value);
    const matched = this.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
    if (matched) {
      this.upsellVariantId = matched.id.toString();
      this.upsellQuantity = 1;
      const hasStock = this._checkInventory(matched, 1);
      this._updatePriceDisplay(matched.price, 1);
      if (hasStock) {
        this._showAvailable();
      } else {
        this._showUnavailable();
      }
    } else {
      this._showUnavailable();
    }
  }

  // ─────────────────────────────────────────────────────────
  // AUTO-MATCH MODE
  // ─────────────────────────────────────────────────────────

  _initAutoMatch() {
    // Listen on the closest shopify-section for the theme's VariantUpdateEvent.
    // The theme fires this event AFTER the section HTML has been fetched and
    // before morphing. event.detail.resource contains the selected variant object.
    const section = this.closest('.shopify-section') || document;

    // Theme event name from assets/events.js (ThemeEvents.variantUpdate)
    const VARIANT_UPDATE_EVENT = 'theme:variant:update';
    const VARIANT_SELECTED_EVENT = 'theme:variant:selected';

    section.addEventListener(VARIANT_UPDATE_EVENT, this._onVariantUpdate.bind(this), {
      signal: this._controller.signal
    });

    // Also listen for the lower-level selected event for immediate UI updates
    // before the section fetch completes (faster perceived response)
    section.addEventListener(VARIANT_SELECTED_EVENT, this._onVariantSelected.bind(this), {
      signal: this._controller.signal
    });

    // Fallback: observe native input changes on the variant picker form
    // This covers cases where the section does not bubble ThemeEvents
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('change', this._onVariantPickerChange.bind(this), {
        signal: this._controller.signal
      });
    }

    // Run on initial load with server-rendered hub size
    const initialSize = this.dataset.hubSize || '';
    if (initialSize) {
      this._updateFromHubSize(initialSize);
    } else {
      // Detect from current picker state
      const detected = this._detectCurrentHubSize();
      if (detected) this._updateFromHubSize(detected);
    }
  }

  /**
   * Fired by the theme after a variant is fully updated (section fetch complete).
   * event.detail.resource = Shopify variant object with .options, .title, etc.
   */
  _onVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;
    const size = this._extractSizeFromVariant(variant);
    if (size) this._updateFromHubSize(size);
  }

  /**
   * Fired by the theme immediately when user selects a variant (before fetch).
   * Gives instant UI update while section re-fetches in the background.
   */
  _onVariantSelected(event) {
    const variant = event.detail?.variant || event.detail?.resource;
    if (!variant) return;
    const size = this._extractSizeFromVariant(variant);
    if (size) this._updateFromHubSize(size);
  }

  /**
   * Fallback: watch for native input/select changes on the variant picker.
   * Debounced to handle rapid changes correctly.
   */
  _onVariantPickerChange() {
    clearTimeout(this._changeTimer);
    this._changeTimer = setTimeout(() => {
      const detected = this._detectCurrentHubSize();
      if (detected) this._updateFromHubSize(detected);
    }, 50);
  }

  /**
   * Extract the "Size" option value from a Shopify variant object.
   * Falls back to parsing the full variant title.
   */
  _extractSizeFromVariant(variant) {
    // Modern Shopify variant objects expose .options as an array
    if (variant.options && Array.isArray(variant.options)) {
      // The HUB 2 has a single "Size" option, so options[0] is the size
      // but we check all options for robustness
      for (const opt of variant.options) {
        const lower = opt.toLowerCase();
        if (
          lower.includes('single') || lower.includes('double') ||
          lower.includes('queen') || lower.includes('king') ||
          lower.includes('super') || lower.includes('split') ||
          lower.includes('long')
        ) {
          return opt;
        }
      }
    }
    // Fallback: parse variant title
    if (variant.title) return variant.title;
    return '';
  }

  /**
   * Read the currently selected size from the variant picker DOM.
   */
  _detectCurrentHubSize() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return '';

    // Radio buttons (button/swatch variant style)
    const fieldsets = variantPicker.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (legend && legend.textContent.toLowerCase().includes('size')) {
        const checked = fieldset.querySelector('input:checked');
        if (checked) return checked.value.trim();
      }
    }

    // Dropdown selects
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      const label = variantPicker.querySelector(`label[for="${select.id}"]`);
      if (label && label.textContent.toLowerCase().includes('size')) {
        return select.value.trim();
      }
    }

    // Fallback: any checked input whose value is a known size keyword
    const allChecked = variantPicker.querySelectorAll('input:checked');
    for (const input of allChecked) {
      const val = (input.value || '').toLowerCase();
      if (
        val.includes('single') || val.includes('double') || val.includes('queen') ||
        val.includes('king') || val.includes('super') || val.includes('split')
      ) {
        return input.value.trim();
      }
    }

    return '';
  }

  /**
   * Core auto-match logic.
   * Given a HUB size string, determines zone type + quantity,
   * resolves the Extension variant, and updates the entire UI.
   * @param {string} hubSize
   */
  _updateFromHubSize(hubSize) {
    if (!hubSize || !this.variants.length) return;

    const { zoneType, quantity } = this._resolveMapping(hubSize);
    const variant = this._findVariantByZone(zoneType);

    if (!variant) {
      console.warn('[HubUpsell] Could not resolve extension variant for zone:', zoneType);
      this._showUnavailable();
      return;
    }

    // Update internal state
    this.upsellVariantId = variant.id.toString();
    this.upsellQuantity = quantity;

    // Check inventory
    const hasStock = this._checkInventory(variant, quantity);

    // Update match label text
    this._updateMatchLabel(zoneType, quantity);

    // Update price
    this._updatePriceDisplay(variant.price, quantity);

    // Update button + unavailable message
    if (hasStock) {
      this._showAvailable();
    } else {
      this._showUnavailable();
    }

    // Reset added state if size changed
    this._resetAddedState();
  }

  /**
   * HUB size → { zoneType: 'single'|'dual', quantity: 1|2 }
   * @param {string} hubSize
   * @returns {{ zoneType: string, quantity: number }}
   */
  _resolveMapping(hubSize) {
    const s = hubSize.toLowerCase();

    // Super King or Split King → Single-Zone × 2
    if (s.includes('super') || s.includes('split')) {
      return { zoneType: 'single', quantity: 2 };
    }

    // Double, Queen, King (but not King Single) → Dual-Zone × 1
    if (
      s.includes('double') ||
      s.includes('queen') ||
      (s.includes('king') && !s.includes('king single') && !s.includes('long'))
    ) {
      return { zoneType: 'dual', quantity: 1 };
    }

    // Single, Long Single, King Single → Single-Zone × 1
    return { zoneType: 'single', quantity: 1 };
  }

  /**
   * Find a variant whose option values include the given zone keyword.
   * Uses fuzzy matching (includes) so "Single-Zone" matches "single".
   * @param {string} zoneType 'single' | 'dual'
   * @returns {object|null}
   */
  _findVariantByZone(zoneType) {
    const keyword = zoneType.toLowerCase(); // 'single' or 'dual'
    return this.variants.find(v => {
      const optsStr = (v.options || []).join(' ').toLowerCase();
      return optsStr.includes(keyword);
    }) || null;
  }

  /**
   * Returns true if the variant has sufficient inventory for the required quantity.
   * @param {object} variant
   * @param {number} quantity
   * @returns {boolean}
   */
  _checkInventory(variant, quantity) {
    if (!variant.available) return false;
    // inventory_quantity may be -1 for "continue selling when out of stock"
    // If it's -1 or null/undefined, treat as available
    if (variant.inventory_quantity === undefined || variant.inventory_quantity === null) return true;
    if (variant.inventory_quantity < 0) return true; // continue selling
    return variant.inventory_quantity >= quantity;
  }

  // ─────────────────────────────────────────────────────────
  // UI UPDATES
  // ─────────────────────────────────────────────────────────

  _updateMatchLabel(zoneType, quantity) {
    if (!this.matchLabelEl) return;
    let text = '';
    if (quantity === 2) {
      text = '2 × Single-Zone Hose Extensions';
    } else if (zoneType === 'dual') {
      text = '1 × Dual-Zone Hose Extension';
    } else {
      text = '1 × Single-Zone Hose Extension';
    }
    this.matchLabelEl.textContent = text;
  }

  _updatePriceDisplay(variantPrice, quantity) {
    if (!this.btnPriceEl) return;
    const totalPrice = variantPrice * quantity;
    const formatted = window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(totalPrice, window.theme?.money_format || '${{amount}}')
      : '$' + (totalPrice / 100).toFixed(2);
    this.btnPriceEl.textContent = formatted;
  }

  _toggleSoldOutBanner(show) {
    const banners = document.querySelectorAll('[data-extension-sold-out-banner], .text-block--text_n6CJma');
    banners.forEach(b => {
      b.style.display = show ? '' : 'none';
    });
  }

  _showAvailable() {
    this.style.display = '';
    if (this.button) this.button.disabled = false;
    if (this.unavailableEl) this.unavailableEl.style.display = 'none';
    this._toggleSoldOutBanner(true);
  }

  _showUnavailable() {
    this.style.display = 'none';
    if (this.button) this.button.disabled = true;
    if (this.unavailableEl) this.unavailableEl.style.display = 'block';
    this._toggleSoldOutBanner(false);
  }

  _resetAddedState() {
    if (!this.button) return;
    this.button.classList.remove('upsell-block__button--added');
    const addText = this.button.querySelector('.upsell-block__btn-text--add');
    const addedText = this.button.querySelector('.upsell-block__btn-text--added');
    if (addText) addText.style.display = '';
    if (addedText) addedText.style.display = '';
  }

  // ─────────────────────────────────────────────────────────
  // CART OPERATIONS
  // ─────────────────────────────────────────────────────────

  getMainVariantId() {
    // Primary: the hidden variant input in the product form
    const variantInput = document.querySelector('form[action="/cart/add"] [name="id"]');
    if (variantInput?.value) return variantInput.value;

    // Secondary: URL param
    const params = new URLSearchParams(window.location.search);
    if (params.get('variant')) return params.get('variant');

    // Tertiary: product JSON block
    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      try {
        const data = JSON.parse(productJson.textContent);
        return data.selected_or_first_available_variant?.id?.toString();
      } catch (_) {}
    }

    return null;
  }

  /**
   * Check if the upsell variant is already in the cart at the required quantity.
   * Returns true if cart already has enough of the item, preventing duplicates.
   */
  async _isUpsellAlreadyInCart() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await res.json();
      const upsellId = parseInt(this.upsellVariantId, 10);
      const cartItem = cart.items.find(item => item.variant_id === upsellId);
      if (!cartItem) return false;
      // For qty 2, verify at least 2 are already present
      return cartItem.quantity >= this.upsellQuantity;
    } catch (_) {
      return false;
    }
  }

  async isVariantInCart(variantId) {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await res.json();
      return cart.items.some(item => item.variant_id.toString() === variantId.toString());
    } catch (_) {
      return false;
    }
  }

  async addToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart.');
    }
    return res.json();
  }

  refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('[data-cart-total]').forEach(el => {
          el.textContent = window.Shopify?.formatMoney
            ? window.Shopify.formatMoney(cart.total_price, window.theme?.money_format)
            : cart.total_price;
        });
        document.querySelectorAll('cart-count').forEach(el => {
          if (typeof el.update === 'function') el.update(cart);
        });
      })
      .catch(() => {});

    // Trigger cart drawer refresh
    const cartDrawer =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      fetch(`${window.Shopify?.routes?.root ?? '/'}?section_id=cart-drawer`)
        .then(r => r.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const sectionHtml = doc.querySelector('#shopify-section-cart-drawer')?.innerHTML;
          if (sectionHtml) {
            cartDrawer.renderContents({ sections: { 'cart-drawer': sectionHtml } });
          }
        })
        .catch(() => {});
    }
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.classList.toggle('loading', isLoading);
    this.button.disabled = isLoading;
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.style.display = 'block';
    setTimeout(() => {
      this.errorEl.style.display = 'none';
    }, 4000);
  }

  _setAddedState() {
    if (!this.button) return;
    this.button.classList.add('upsell-block__button--added');
    this.button.disabled = false;
    const addText = this.button.querySelector('.upsell-block__btn-text--add');
    const addedText = this.button.querySelector('.upsell-block__btn-text--added');
    if (addText) addText.style.display = 'none';
    if (addedText) addedText.style.display = 'inline';
  }

  // ─────────────────────────────────────────────────────────
  // CLICK HANDLER
  // ─────────────────────────────────────────────────────────

  async _handleClick() {
    if (!this.upsellVariantId) {
      this.showError('Upsell product is unavailable.');
      return;
    }

    this.setLoading(true);
    if (this.errorEl) this.errorEl.style.display = 'none';

    try {
      const mainVariantId = this.getMainVariantId();

      if (!mainVariantId) {
        this.showError('Please select a variant first.');
        return;
      }

      // Duplicate check: is the upsell already in cart at the required quantity?
      const alreadyAdded = await this._isUpsellAlreadyInCart();
      if (alreadyAdded) {
        this._setAddedState();
        return;
      }

      // Determine if the main HUB is already in the cart
      const mainAlreadyInCart = await this.isVariantInCart(mainVariantId);

      // Determine the current HUB size for the line item property
      const hubSize = this._detectCurrentHubSize() || this.dataset.hubSize || '';

      // Build cart items
      const upsellItem = {
        id: parseInt(this.upsellVariantId, 10),
        quantity: this.upsellQuantity,
        properties: {
          // Prefixed with _ to suppress display in most Shopify themes
          '_Matched to': hubSize || 'HUB 2',
          '_Upsell': 'HUB Hose Extension'
        }
      };

      const itemsToAdd = mainAlreadyInCart
        ? [upsellItem]
        : [
            { id: parseInt(mainVariantId, 10), quantity: 1 },
            upsellItem
          ];

      await this.addToCart(itemsToAdd);
      this.refreshCart();
      this._setAddedState();

    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again.');
      console.error('[HubUpsell]', err);
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('upsell-product')) {
  customElements.define('upsell-product', UpsellProduct);
}